import type { ReactNode } from "react";
import { useOstendoLiveArms, useOstendoLiveFailedArms, type OstendoArm } from "@/lib/ostendo-live-source";

/** Parse Ostendo's dd/mm/yyyy snapshot date → "16 Apr" (en-AU short). */
export function formatSnapshotDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

export function FreshnessBadge({
  label,
  value,
  live,
  title,
  unreachable,
  liveUnavailable,
  pulse = true,
}: {
  label: string;
  /** Usually plain text; a page whose value has independently-wrappable parts
   * (SCHEDDOC's "13:20 today · hourly") may pass its own flex-wrap markup
   * instead — the colour class below still applies to it as a whole. */
  value: ReactNode;
  live: boolean;
  title: string;
  unreachable?: boolean;
  /** #3010: the page wanted live, live failed, and the copy answered. */
  liveUnavailable?: boolean;
  /** The ping animation on a live dot. Off for the Ostendo arms (#1798): a still dot plus the word. */
  pulse?: boolean;
}) {
  const dotClass = unreachable ? "bg-red-500" : liveUnavailable ? "bg-orange-500" : live ? "bg-emerald-500" : "bg-amber-500";
  const valueClass = unreachable
    ? "text-red-600 dark:text-red-400"
    : liveUnavailable
      ? "font-medium text-orange-700 dark:text-orange-400"
      : live
        ? "text-emerald-600 dark:text-emerald-400"
        : "text-amber-600 dark:text-amber-400/90";
  return (
    <span
      className="inline-flex items-center gap-1.5"
      title={title}
      data-freshness-value={typeof value === "string" ? value : undefined}
    >
      <span className="relative flex h-2 w-2">
        {live && pulse && !unreachable && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        )}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${dotClass}`} />
      </span>
      <span className="shrink-0 whitespace-nowrap text-muted-foreground">{label}:</span>
      <span className={valueClass}>{value}</span>
    </span>
  );
}

/** #3010: the words a page shows when live failed and the copy answered. */
export function liveUnavailableText(copyDate: string | null): string {
  return copyDate ? `live unavailable, copy of ${copyDate}` : "live unavailable, daily copy";
}

const ARM_COPY: Record<OstendoArm, { label: string; live: string; liveUnavailable: string; unreachable: string; copy: string }> = {
  COOP: {
    label: "Ostendo COOP",
    live: "Coopers manufacturing data, read from Ostendo live on this page.",
    liveUnavailable: "Coopers manufacturing data. This page reads Ostendo live, but the live read failed, so it is showing the daily copy up to this date (GH #3010).",
    unreachable: "Coopers — manufacturing/production data. Ostendo API unreachable — key rejected or outage.",
    copy: "Coopers — manufacturing/production data (feeds manufacturing & inventory views). Daily-refreshed replica of the live ERP — contains nothing after this date, so on a Monday it reads Friday’s close (GH #1798).",
  },
  CROS: {
    label: "Ostendo CROS",
    live: "Crosbie National sales data, read from Ostendo live on this page.",
    liveUnavailable: "Crosbie National sales data. This page reads Ostendo live, but the live read failed, so it is showing the daily copy up to this date (GH #3010).",
    unreachable: "Crosbie National — sales/commercial data. Ostendo API unreachable — key rejected or outage. Trace/recall customer legs are unavailable.",
    copy: "Crosbie National — sales/commercial data (feeds the Daily Dashboard & trace/recall customer legs). Daily-refreshed replica of the live ERP — contains nothing after this date, so on a Monday it reads Friday’s close (GH #1798).",
  },
};

/**
 * One Ostendo arm's header badge. #1798: when the current page read this arm
 * live (lib/ostendo-live-source, every reporter on the arm agreeing), the
 * badge says "Live" with a still dot and never the copy's date, even when no
 * copy date is known; the copy date and the unreachable state are unchanged
 * elsewhere. Always mount it: it renders nothing itself when there is nothing
 * to say for the arm, and `wrap` puts the divider and layout span around a
 * badge that does render.
 */
export function OstendoArmBadge({
  arm,
  snapshot,
  unreachable,
  wrap = (badge) => badge,
}: {
  arm: OstendoArm;
  snapshot: string | null | undefined;
  unreachable?: boolean;
  wrap?: (badge: ReactNode) => ReactNode;
}) {
  const liveArms = useOstendoLiveArms();
  const failedArms = useOstendoLiveFailedArms();
  const copy = ARM_COPY[arm];
  const date = formatSnapshotDate(snapshot);
  if (liveArms[arm]) return wrap(<FreshnessBadge label={copy.label} value="Live" live pulse={false} title={copy.live} />);
  // #3010: the page's own response says live failed and the copy answered.
  // Ahead of `unreachable` (the health poll's view of the copy): this page
  // just got an answer from the copy, so the fresher fact is that live is down.
  if (failedArms[arm]) return wrap(<FreshnessBadge label={copy.label} value={liveUnavailableText(date)} live={false} liveUnavailable title={copy.liveUnavailable} />);
  if (unreachable) return wrap(<FreshnessBadge label={copy.label} value="unreachable" live={false} unreachable title={copy.unreachable} />);
  if (!date) return null;
  return wrap(<FreshnessBadge label={copy.label} value={date} live={false} title={copy.copy} />);
}
